import { useEffect } from 'react'
import { usePostHog } from '@posthog/react'
import { useSession } from '@/features/auth/components/SessionProvider'

/**
 * Ties PostHog events to the authenticated user WITHOUT a tracking cookie.
 *
 * Analytics runs cookieless (`persistence: 'memory'`, see main.tsx), so there is
 * no persistent anonymous id to stitch sessions. For signed-in users we instead
 * call `identify(user.id)` explicitly — the user id is a stable first-party
 * identifier we already hold, so funnels (activation, trial→paid) work without
 * setting any cookie. `reset()` on logout clears the in-memory identity.
 *
 * No-ops outside production (no PostHogProvider is mounted in dev / native — see
 * main.tsx). Person properties are intentionally non-PII (no email/name).
 */
export function AnalyticsIdentify() {
  const posthog = usePostHog()
  const { status, profile } = useSession()

  useEffect(() => {
    if (!posthog || !import.meta.env.PROD) return
    if (status === 'authenticated' && profile?.id) {
      posthog.identify(profile.id, {
        loginProvider: profile.loginProvider ?? 'magic_link',
        isPaidAccount: profile.isPaidAccount,
      })
    } else if (status === 'unauthenticated') {
      posthog.reset()
    }
  }, [posthog, status, profile?.id, profile?.loginProvider, profile?.isPaidAccount])

  return null
}
